'use client';

import { useState } from 'react';
import { Users, UserPlus, X } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { usePageBuilderContext } from '../page-builder-context';
import { InviteUserDialog } from '@/components/products/invite-user-dialog';
import type { PendingInvitation } from '@/lib/types/page-builder';

// ============================================================================
// PENDING MEMBER CARD
// ============================================================================

interface PendingMemberCardProps {
  invitation: PendingInvitation;
  onRemove: () => void;
}

function PendingMemberCard({ invitation, onRemove }: PendingMemberCardProps) {
  const { user, role } = invitation;

  return (
    <div className="flex items-center gap-3 p-3 rounded-lg border border-border bg-muted/30">
      <Avatar className="h-10 w-10">
        {user.avatarUrl && (
          <AvatarImage src={user.avatarUrl} alt={user.name} />
        )}
        <AvatarFallback>
          {user.name.slice(0, 2).toUpperCase()}
        </AvatarFallback>
      </Avatar>

      <div className="flex-1 min-w-0">
        <div className="font-medium text-sm text-foreground truncate">
          {user.name}
        </div>
        <div className="text-xs text-foreground-tertiary truncate">
          {role}
        </div>
      </div>

      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="h-8 w-8 text-foreground-tertiary hover:text-destructive"
        onClick={onRemove}
      >
        <X className="h-4 w-4" />
      </Button>
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function TeamSection() {
  const { formData, addPendingInvitation, removePendingInvitation } = usePageBuilderContext();
  const [dialogOpen, setDialogOpen] = useState(false);

  const pendingInvitations = formData.metadata.pendingInvitations || [];
  const pendingUserIds = pendingInvitations.map((inv) => inv.user.id);

  return (
    <div className="bg-card border border-border rounded-xl p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-foreground mb-1">
            Команда
          </h2>
          <p className="text-sm text-foreground-secondary">
            Пригласите участников команды
          </p>
        </div>

        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setDialogOpen(true)}
        >
          <UserPlus className="h-4 w-4 mr-2" />
          Пригласить
        </Button>
      </div>

      {pendingInvitations.length > 0 ? (
        <div className="space-y-2">
          {pendingInvitations.map((inv) => (
            <PendingMemberCard
              key={inv.user.id}
              invitation={inv}
              onRemove={() => removePendingInvitation(inv.user.id)}
            />
          ))}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-8 text-center">
          <Users className="h-10 w-10 text-foreground-tertiary mb-3" />
          <p className="text-sm text-foreground-secondary">
            Пока никого нет
          </p>
          <p className="text-xs text-foreground-tertiary mt-1">
            Добавьте участников команды
          </p>
        </div>
      )}

      <p className="text-xs text-foreground-tertiary">
        Приглашения будут отправлены после публикации продукта
      </p>

      <InviteUserDialog
        mode="deferred"
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        existingMemberIds={[]}
        pendingInvitationUserIds={pendingUserIds}
        onUserSelected={(user, role) => {
          addPendingInvitation(user, role);
          setDialogOpen(false);
        }}
      />
    </div>
  );
}
